import React, { useState } from "react";
import { createPortal } from "react-dom";
import { PathwayFormGroup, PathwayLabel, PathwayStyledInput } from "../../StyledComponents/PathwayForm";

import PropTypes from "prop-types";
import styled from "styled-components";
import usePathwayInputGroup from "../../../_hooks/usePathwayInputGroup";
import MandatoryStar from "../MandatoryStar";
import { PathwayTooltip } from "../PathwayTooltip";
import { PathwayEditButton } from "../PathwayButtons";
import { TOOLTIP_SURFACE } from "../../StyledComponents/tooltipSurface";

const PathwayTextInputGroup = (props) => {
    const [inputTooltip, setInputTooltip] = useState(null);
    const [errorTooltip, setErrorTooltip] = useState(null);

    const handleInputMouseEnter = (e) => {
        const el = e.currentTarget;
        const isTruncated = el.scrollWidth > el.offsetWidth;
        const text = el.value || "";
        const placeholder = el.placeholder || "";
        if (isTruncated && text) {
            const r = el.getBoundingClientRect();
            setInputTooltip({ text, top: r.bottom + 4, left: r.left, width: r.width });
        } else if (!text && placeholder) {
            const r = el.getBoundingClientRect();
            setInputTooltip({ text: placeholder, top: r.bottom + 4, left: r.left, width: r.width });
        }
    };

    const handleInputMouseLeave = () => setInputTooltip(null);

    const handleErrorIconMouseEnter = (e, errorText) => {
        const r = e.currentTarget.getBoundingClientRect();
        setErrorTooltip({ text: errorText, top: r.bottom + 6, left: r.left });
    };

    const handleErrorIconMouseLeave = () => setErrorTooltip(null);

    const { valid, blurredAfterInvalid, mandatory, isNotFilled, handleChange, onBlur, inputRef } = usePathwayInputGroup(
        props.name,
        props.value,
        props.mandatory,
        props.submitted,
        props.autoFocus,
        props.triggerOnBlur,
        props.inputValidation,
        props.handleChange
    );

    const errorText = (() => {
        if (mandatory && isNotFilled && props.invalidInputMessage && !props.inputValidation) {
            return props.invalidInputMessage;
        }
        if (mandatory && isNotFilled) {
            return "Das Feld wird benötigt.";
        }
        if (!valid) {
            return props.invalidInputMessage || null;
        }
        return null;
    })();

    const inputProps = {
        className: props.mono ? "pw-mono" : undefined,
        type: props.type ? props.type : "text",
        name: props.name,
        "data-cy": props.dataCy,
        placeholder: props.placeHolder,
        value: props.value || "",
        disabled: props.disabled,
        pattern: props.pattern,
        maxLength: props.maxLength,
        onChange: handleChange,
        onBlur: onBlur,
        min: props.min,
        max: props.max,
        autoComplete: props.autoComplete,
        onMouseEnter: handleInputMouseEnter,
        onMouseLeave: handleInputMouseLeave,
        // Extra right padding when the error icon is visible so text doesn't overlap it
        style: errorText ? { paddingRight: "2rem" } : undefined,
    };

    return (
        <CustomFormGroup>
            {props.label && (
                <>
                    <TooltipInputGroup>
                        <LabelWrapper data-cy={props.dataCy ? props.dataCy + "-label" : undefined}>
                            <LabelParagraph>{props.label}</LabelParagraph> {mandatory && <MandatoryStar />}
                        </LabelWrapper>
                        {props.tooltip && (
                            <PathwayTooltip id={props.dataCy + "-tooltip"} iconSize="var(--iconSize-xs)">
                                {props.tooltip}
                            </PathwayTooltip>
                        )}
                        {props.handleAction && <PathwayEditButton onClick={props.handleAction} />}
                    </TooltipInputGroup>
                </>
            )}

            <InputWrapper>
                {blurredAfterInvalid ? (
                    <PathwayStyledInput {...inputProps} valid={valid} invalid={!valid} />
                ) : (
                    <PathwayStyledInput {...inputProps} invalid={!valid} innerRef={inputRef} />
                )}

                {errorText && (
                    <ErrorIcon
                        type="button"
                        data-cy={props.dataCy ? props.dataCy + "-error-icon" : undefined}
                        onMouseEnter={(e) => handleErrorIconMouseEnter(e, errorText)}
                        onMouseLeave={handleErrorIconMouseLeave}
                        onFocus={(e) => handleErrorIconMouseEnter(e, errorText)}
                        onBlur={handleErrorIconMouseLeave}
                        aria-label={errorText}
                    >
                        <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
                    </ErrorIcon>
                )}
            </InputWrapper>

            {inputTooltip &&
                createPortal(
                    <div
                        style={{
                            position: "fixed",
                            top: inputTooltip.top,
                            left: inputTooltip.left,
                            minWidth: inputTooltip.width,
                            maxWidth: Math.max(inputTooltip.width, 320),
                            zIndex: 99999,
                            padding: "4px 10px",
                            pointerEvents: "none",
                            wordBreak: "break-word",
                            ...TOOLTIP_SURFACE,
                        }}
                    >
                        {inputTooltip.text}
                    </div>,
                    document.body
                )}

            {errorTooltip &&
                createPortal(
                    <div
                        style={{
                            position: "fixed",
                            top: errorTooltip.top,
                            left: errorTooltip.left,
                            zIndex: 99999,
                            padding: "4px 10px",
                            maxWidth: 320,
                            pointerEvents: "none",
                            wordBreak: "break-word",
                            ...TOOLTIP_SURFACE,
                            backgroundColor: "var(--pw-danger)",
                        }}
                    >
                        {errorTooltip.text}
                    </div>,
                    document.body
                )}
        </CustomFormGroup>
    );
};

export default PathwayTextInputGroup;

PathwayTextInputGroup.propTypes = {
    label: PropTypes.string,
    name: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    handleChange: PropTypes.func.isRequired,
    dataCy: PropTypes.string,
    mono: PropTypes.bool,
    pattern: PropTypes.string,
    maxLength: PropTypes.string,
    placeHolder: PropTypes.string,
    tooltip: PropTypes.node,
    invalidInputMessage: PropTypes.any,
    mandatory: PropTypes.bool,
    autoFocus: PropTypes.bool,
    disabled: PropTypes.bool,
    inputValidation: PropTypes.func,
    triggerOnBlur: PropTypes.func,
    invalidMessageSmall: PropTypes.bool,
    autoComplete: PropTypes.string,
};

const CustomFormGroup = styled(PathwayFormGroup)`
    justify-items: normal;
    margin-bottom: 0 !important;
`;

const InputWrapper = styled.div`
    position: relative;
    width: 100%;
`;

const ErrorIcon = styled.button`
    position: absolute;
    right: 9px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    background: none;
    color: var(--pw-danger-fg);
    font-size: 13px;
    cursor: default;
    line-height: 1;
`;

const LabelWrapper = styled(PathwayLabel)`
    display: grid;
    grid-template-columns: auto min-content;
    align-items: center;
`;

const LabelParagraph = styled.p`
    margin: 0;
    font-size: var(--pw-text-sm);
    font-weight: ${(props) => (props.strong ? "var(--pw-weight-semibold)" : "var(--pw-weight-regular)")};
    color: var(--text-heading);
    line-height: var(--pw-leading-snug);
`;

const TooltipInputGroup = styled.div`
    display: grid;
    grid-template-columns: max-content min-content min-content;
    column-gap: var(--gap-s);
`;

// Kept for a11y: screen readers still announce the error via the red border's
// aria-invalid attribute. The InvalidLabel is no longer rendered visually.
// eslint-disable-next-line no-unused-vars
const InvalidLabel = styled(PathwayLabel)`
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    margin: 0;
    padding-top: var(--gap-s);
    color: var(--pw-danger-fg);
    font-size: var(--pw-text-sm);
    font-weight: var(--pw-weight-regular);
`;
